import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, X } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { ReadOnlyNotice } from '@/components/shared/read-only-notice';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toast';
import { useIsPlatformAdmin } from '@/hooks/use-is-platform-admin';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { uploadBlob } from '@/lib/upload';
import { cn } from '@/utils/cn';

const TYPES = [
  'sermon',
  'devotional',
  'announcement',
  'video',
  'gallery',
  'event',
  'service',
] as const;

type DefaultImageType = (typeof TYPES)[number];

interface DefaultImageSlot {
  type: DefaultImageType;
  key: string | null;
  url: string | null;
}

const ImageTile = ({
  slot,
  onUploaded,
  onRemoved,
  busy,
}: {
  slot: DefaultImageSlot;
  onUploaded: (key: string) => void;
  onRemoved: () => void;
  busy: boolean;
}) => {
  const { t } = useTranslation('admin');
  const { t: tErrors } = useTranslation('errors');
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <FormField label={t(`defaultImages.types.${slot.type}`)}>
      {() => (
        <div className="relative">
          <button
            type="button"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            aria-label={slot.url ? t('defaultImages.change') : t('defaultImages.add')}
            className={cn(
              'group relative aspect-square w-full overflow-hidden rounded-lg border bg-surface-sunken transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
              slot.url ? 'border-border' : 'border-dashed border-border-strong',
              busy ? 'cursor-not-allowed opacity-50' : 'hover:border-brand/50',
            )}
          >
            {slot.url ? (
              <img src={slot.url} alt="" className="size-full object-cover" />
            ) : (
              <span className="flex size-full items-center justify-center text-fg-tertiary">
                <Camera className="size-6" aria-hidden />
              </span>
            )}
            <span
              aria-hidden
              className={cn(
                'absolute inset-0 flex items-center justify-center bg-overlay/55 text-fg-inverse opacity-0 transition-opacity',
                'group-hover:opacity-100 group-focus-visible:opacity-100',
              )}
            >
              <Camera className="size-5" />
            </span>
          </button>
          {slot.key ? (
            <Button
              type="button"
              variant="secondary"
              size="icon"
              disabled={busy}
              aria-label={t('defaultImages.remove')}
              className="absolute top-1.5 right-1.5 size-7 shadow-e1"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onRemoved();
              }}
            >
              <X className="size-3.5" aria-hidden />
            </Button>
          ) : null}
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (!file) return;
              void (async () => {
                try {
                  const { key } = await uploadBlob({
                    purpose: 'platform_content_default',
                    blob: file,
                  });
                  onUploaded(key);
                } catch (err) {
                  toast.error(
                    err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'),
                  );
                }
              })();
            }}
          />
        </div>
      )}
    </FormField>
  );
};

export const DefaultImagesPage = () => {
  const { t } = useTranslation('admin');
  const isAdmin = useIsPlatformAdmin();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const defaults = useQuery({
    queryKey: ['platform-content-defaults'],
    queryFn: () => api.get<{ data: DefaultImageSlot[] }>('/platform/content-defaults'),
  });

  const save = useMutation({
    mutationFn: (patch: Partial<Record<DefaultImageType, string | null>>) =>
      api.patch<{ data: DefaultImageSlot[] }>('/platform/content-defaults', patch),
    onSuccess: async (result, patch) => {
      queryClient.setQueryData(['platform-content-defaults'], result);
      const removed = Object.values(patch).every((value) => value == null);
      toast.success(removed ? t('defaultImages.removed') : t('defaultImages.saved'));
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const slots = defaults.data?.data ?? [];

  return (
    <div>
      <PageHeader title={t('defaultImages.title')} description={t('defaultImages.description')} />
      <ReadOnlyNotice />
      <Card>
        <CardContent className="grid grid-cols-2 gap-4 p-6 sm:grid-cols-3 lg:grid-cols-4">
          {defaults.isLoading
            ? TYPES.map((type) => (
                <div key={type} className="space-y-1.5">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="aspect-square w-full" />
                </div>
              ))
            : slots.map((slot) => (
                <ImageTile
                  key={slot.type}
                  slot={slot}
                  busy={!isAdmin || save.isPending}
                  onUploaded={(key) => save.mutate({ [slot.type]: key })}
                  onRemoved={() => save.mutate({ [slot.type]: null })}
                />
              ))}
        </CardContent>
      </Card>
    </div>
  );
};
